/** Juno-60 style chorus effect — BBD emulation using modulated delay lines.
 *
 * The Juno-60 chorus uses two modulated delay lines (BBD) to create ensemble
 * movement. Three modes are available:
 * - **Type I**: Slow, gentle chorus (single slow LFO)
 * - **Type II**: Faster, more pronounced chorus (single fast LFO)
 * - **I+II**: Both simultaneously for a thick, lush ensemble effect
 *
 * Audio chain:
 *   input → dryGain ─────────────────────────────┐
 *   input → delayL → wetGainL ─→ merger(L) ──────├→ output
 *   input → delayR → wetGainR ─→ merger(R) ──────┘
 *          ↑ modulated by lfoL and lfoR
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Chorus mode matching the Juno-60 switch positions. */
export type ChorusMode = "I" | "II" | "I+II";

/** Chorus effect parameters. */
export type ChorusParams = {
	/** Whether the chorus effect is active. */
	readonly enabled: boolean;
	/** Chorus mode: I (slow), II (fast), or I+II (both). */
	readonly mode: ChorusMode;
};

/** Default chorus parameters. */
export const DEFAULT_CHORUS: ChorusParams = {
	enabled: false,
	mode: "I",
} as const;

/** All available chorus modes. */
export const CHORUS_MODES: readonly ChorusMode[] = ["I", "II", "I+II"] as const;

/** Display labels for chorus modes. */
export const CHORUS_MODE_LABELS: Readonly<Record<ChorusMode, string>> = {
	I: "I – Subtle",
	II: "II – Rich",
	"I+II": "I+II – Lush",
} as const;

// ---------------------------------------------------------------------------
// Juno-60 chorus parameters per mode
//
// The original Juno-60 uses a single MN3009 BBD with ~4ms base delay.
// LFO modulates the delay time ±0.5–1.5ms around the centre.
// Type I: slow rate (~0.5 Hz), shallow depth
// Type II: faster rate (~0.83 Hz), deeper depth
// I+II: both LFO rates simultaneously (dual chorus lines)
// ---------------------------------------------------------------------------

type ModeSettings = {
	readonly rateL: number; // Hz — LFO rate for left channel
	readonly rateR: number; // Hz — LFO rate for right channel
	readonly depthL: number; // seconds — modulation depth (peak)
	readonly depthR: number; // seconds — modulation depth (peak)
	readonly baseDelay: number; // seconds — centre delay time
	readonly mix: number; // wet/dry ratio (0–1)
};

const MODE_SETTINGS: Readonly<Record<ChorusMode, ModeSettings>> = {
	I: {
		rateL: 0.513,
		rateR: 0.513,
		depthL: 0.0005,
		depthR: 0.0005,
		baseDelay: 0.004,
		mix: 0.5,
	},
	II: {
		rateL: 0.863,
		rateR: 0.863,
		depthL: 0.001,
		depthR: 0.001,
		baseDelay: 0.004,
		mix: 0.5,
	},
	"I+II": {
		rateL: 0.513,
		rateR: 0.863,
		depthL: 0.0006,
		depthR: 0.001,
		baseDelay: 0.004,
		mix: 0.5,
	},
} as const;

// ---------------------------------------------------------------------------
// Chorus processor
// ---------------------------------------------------------------------------

/** Juno-60 style chorus effect using modulated delay lines. */
export class Chorus {
	private readonly context: AudioContext;
	private readonly inputNode: GainNode;
	private readonly outputNode: GainNode;
	private readonly dryGain: GainNode;
	private readonly wetGainL: GainNode;
	private readonly wetGainR: GainNode;
	private readonly delayL: DelayNode;
	private readonly delayR: DelayNode;
	private readonly lfoL: OscillatorNode;
	private readonly lfoR: OscillatorNode;
	private readonly lfoGainL: GainNode;
	private readonly lfoGainR: GainNode;
	private readonly merger: ChannelMergerNode;
	private params: ChorusParams;

	constructor(context: AudioContext) {
		this.context = context;
		this.params = { ...DEFAULT_CHORUS };

		// Create nodes
		this.inputNode = context.createGain();
		this.outputNode = context.createGain();
		this.dryGain = context.createGain();
		this.wetGainL = context.createGain();
		this.wetGainR = context.createGain();

		// Delay lines (max 10ms, Juno uses ~4ms centre)
		this.delayL = context.createDelay(0.02);
		this.delayR = context.createDelay(0.02);

		// LFO oscillators for delay modulation
		this.lfoL = context.createOscillator();
		this.lfoR = context.createOscillator();
		this.lfoL.type = "sine";
		this.lfoR.type = "sine";

		// LFO gain nodes (control modulation depth)
		this.lfoGainL = context.createGain();
		this.lfoGainR = context.createGain();

		// Stereo merger
		this.merger = context.createChannelMerger(2);

		// Wire dry path: input → dryGain → output
		this.inputNode.connect(this.dryGain);
		this.dryGain.connect(this.outputNode);

		// Wire wet path: input → delayL/R → wetGainL/R → merger → output
		this.inputNode.connect(this.delayL);
		this.inputNode.connect(this.delayR);
		this.delayL.connect(this.wetGainL);
		this.delayR.connect(this.wetGainR);
		this.wetGainL.connect(this.merger, 0, 0); // left channel
		this.wetGainR.connect(this.merger, 0, 1); // right channel
		this.merger.connect(this.outputNode);

		// Wire LFOs to delay time params
		this.lfoL.connect(this.lfoGainL);
		this.lfoR.connect(this.lfoGainR);
		this.lfoGainL.connect(this.delayL.delayTime);
		this.lfoGainR.connect(this.delayR.delayTime);

		// Start LFOs
		this.lfoL.start();
		this.lfoR.start();

		// Apply initial settings
		this.applyParams();
	}

	/** The node to connect audio sources into. */
	get input(): GainNode {
		return this.inputNode;
	}

	/** The node to connect to downstream effects or destination. */
	get output(): GainNode {
		return this.outputNode;
	}

	/** Current chorus parameters. */
	get currentParams(): ChorusParams {
		return this.params;
	}

	/** Update chorus parameters. */
	setParams(update: Partial<ChorusParams>): void {
		this.params = { ...this.params, ...update };
		this.applyParams();
	}

	/** Apply current params to Web Audio nodes. */
	private applyParams(): void {
		const now = this.context.currentTime;
		const settings = MODE_SETTINGS[this.params.mode];

		if (!this.params.enabled) {
			// Bypass: dry = 1, wet = 0
			this.dryGain.gain.setTargetAtTime(1.0, now, 0.01);
			this.wetGainL.gain.setTargetAtTime(0, now, 0.01);
			this.wetGainR.gain.setTargetAtTime(0, now, 0.01);
			return;
		}

		// Set LFO rates
		this.lfoL.frequency.setTargetAtTime(settings.rateL, now, 0.01);
		this.lfoR.frequency.setTargetAtTime(settings.rateR, now, 0.01);

		// Set modulation depth
		this.lfoGainL.gain.setTargetAtTime(settings.depthL, now, 0.01);
		this.lfoGainR.gain.setTargetAtTime(settings.depthR, now, 0.01);

		// Set base delay times
		this.delayL.delayTime.setTargetAtTime(settings.baseDelay, now, 0.01);
		this.delayR.delayTime.setTargetAtTime(settings.baseDelay, now, 0.01);

		// Set wet/dry mix
		this.dryGain.gain.setTargetAtTime(1.0 - settings.mix * 0.5, now, 0.01);
		this.wetGainL.gain.setTargetAtTime(settings.mix, now, 0.01);
		this.wetGainR.gain.setTargetAtTime(settings.mix, now, 0.01);
	}

	/** Disconnect all internal nodes (for cleanup). */
	dispose(): void {
		try {
			this.lfoL.stop();
			this.lfoR.stop();
		} catch {
			// Already stopped
		}
		this.inputNode.disconnect();
		this.outputNode.disconnect();
		this.dryGain.disconnect();
		this.wetGainL.disconnect();
		this.wetGainR.disconnect();
		this.delayL.disconnect();
		this.delayR.disconnect();
		this.lfoL.disconnect();
		this.lfoR.disconnect();
		this.lfoGainL.disconnect();
		this.lfoGainR.disconnect();
		this.merger.disconnect();
	}
}
